import { ENVIRONMENT, FeatureManager, MixpanelClient } from '@enaratech/funnel-helper';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './components/App/App';
import UnhandledError from './components/Common/UnhandledError/UnhandledError';
import { AppProvider } from './contexts/app';
import { AuthProvider } from './contexts/auth';
import { ClinicProvider } from './contexts/clinic';
import { ExperimentProvider } from './contexts/experiments';
import { OnboardingProvider } from './contexts/onboarding';
import { SSBProvider } from './contexts/ssb';
import './index.css';
import reportWebVitals from './reportWebVitals';
import theme from './theme';

// Sentry DSN from environment variable
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

// HIPAA: some backend calls carry credentials or contact info in the query
// string (e.g. auth_token via authAs:'params'); breadcrumb/request URLs must
// never ship them to Sentry.
const scrubSensitiveUrl = (url: string): string =>
  url.replace(/([?&](?:auth_token|token|email|phone)=)[^&#]*/gi, '$1[redacted]');

const scrubUser = <T extends { user?: Sentry.User }>(event: T): T => {
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
  }

  return event;
};

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserProfilingIntegration(),
      // send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = scrubSensitiveUrl(event.request.url);
      }

      return scrubUser(event);
    },
    // Transactions do not pass through beforeSend
    beforeSendTransaction(event) {
      return scrubUser(event);
    },
    beforeBreadcrumb(breadcrumb) {
      if (typeof breadcrumb.data?.url === 'string') {
        breadcrumb.data.url = scrubSensitiveUrl(breadcrumb.data.url);
      }

      return breadcrumb;
    },
  });
}

MixpanelClient.init();
FeatureManager.initVariant();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <AppProvider>
          <SSBProvider>
            <ClinicProvider>
              <OnboardingProvider>
                <AuthProvider>
                  <ExperimentProvider>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                      <Sentry.ErrorBoundary fallback={<UnhandledError />}>
                        <App />
                      </Sentry.ErrorBoundary>
                    </LocalizationProvider>
                  </ExperimentProvider>
                </AuthProvider>
              </OnboardingProvider>
            </ClinicProvider>
          </SSBProvider>
        </AppProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
